import React, { useState } from 'react';
import './Kalkulator.css';

// Heuristička funkcija za genitiv (BCS jezici)
function declineGenitive(text) {
  return text.trim().split(' ').map(word => {
    if (!word) return '';
    const lastChar = word.slice(-1).toLowerCase();
    const stem = word.slice(0, -1);
    let gen;
    switch (lastChar) {
      case 'a': gen = stem + 'e'; break;
      case 'o': gen = stem + 'a'; break;
      case 'e': gen = stem + 'a'; break;
      default: gen = word + 'a'; break;
    }
    return gen.charAt(0).toUpperCase() + gen.slice(1);
  }).join(' ');
}

export default function UgovorORaduGenerator() {
  const [entity, setEntity] = useState('FBIH');
  const [data, setData] = useState({
    employer: '', employerJIB: '', employerAddress: '',
    employee: '', employeeJMBG: '', employeeAddress: '',
    jobTitle: '', workplace: '',
    contractCategory: 'ugovor o radu na neodređeno vrijeme',
    startDate: '', endDate: '',
    salary: '', salaryType: 'neto', salaryPeriod: 'mjesečno',
    probation: false, probationPeriod: '',
    noticePeriod: '', confidentiality: false,
    employerObligations: '', employeeObligations: '',
    annualLeave: '', overtimePolicy: '', travelExpenses: ''
  });

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const buildContract = () => {
    const {
      employer, employerJIB, employerAddress,
      employee, employeeJMBG, employeeAddress,
      jobTitle, workplace, contractCategory,
      startDate, endDate, salary, salaryType,
      salaryPeriod, probation, probationPeriod,
      noticePeriod, confidentiality,
      employerObligations, employeeObligations,
      annualLeave, overtimePolicy, travelExpenses
    } = data;

    const empGen = declineGenitive(employer);
    const emp2Gen = declineGenitive(employee);
    const title = contractCategory.toUpperCase();
    const headerLaw = entity === 'FBIH'
      ? 'prema Zakonu o radu FBiH i važećim propisima'
      : 'prema Zakonu o radu Republike Srpske i propisima';

    let text = `${title} (${entity})\n\n`;
    text += `Zaključen dana ${startDate}. godine ${headerLaw}:\n` +
      `Poslodavac ${empGen}, JIB: ${employerJIB}, sa sjedištem: ${employerAddress}\n` +
      `i Radnik ${emp2Gen}, JMBG: ${employeeJMBG}, sa adresom: ${employeeAddress}\n\n`;

    // Član 1: vrsta i trajanje
    text += `Član 1. (vrsta ugovora) Ovaj ${contractCategory}` +
      (contractCategory.includes('određeno') ? ` zaključen do ${endDate}` : '') +
      `.\n\n`;

    let idx = 2;
    // Probni rad
    if (probation) {
      text += `Član ${idx}. (probni rad) Probni rad traje ${probationPeriod} dana.\n\n`;
      idx++;
    }
    // Član radno vrijeme
    text += `Član ${idx}. (radno vrijeme) Radnik radi puno radno vrijeme.\n\n`;
    idx++;
    // Član plata
    text += `Član ${idx}. (plata) Radnik će primati ${salaryType} platu od ${salary} KM (${salaryPeriod}).\n\n`;
    idx++;
    // Član otkazni rok
    text += `Član ${idx}. (otkazni rok) Otkazni rok iznosi ${noticePeriod} dana.\n\n`;
    idx++;
    // Član prava i obaveze poslodavca
    text += `Član ${idx}. (prava i obaveze poslodavca) ${employerObligations}.\n\n`;
    idx++;
    // Član prava i obaveze radnika
    text += `Član ${idx}. (prava i obaveze radnika) ${employeeObligations}.\n\n`;
    idx++;
    // Član godišnji odmor
    text += `Član ${idx}. (godišnji odmor) Radnik ima pravo na ${annualLeave} dana godišnjeg odmora godišnje.\n\n`;
    idx++;
    // Član prekovremeni rad
    text += `Član ${idx}. (prekovremeni rad) ${overtimePolicy}.\n\n`;
    idx++;
    // Član putni troškovi
    text += `Član ${idx}. (putni troškovi) ${travelExpenses}.\n\n`;
    idx++;
    // Član povjerljivost
    if (confidentiality) {
      text += `Član ${idx}. (povjerljivost) Strane čuvaju poslovnu tajnu.\n\n`;
      idx++;
    }

    // Zaključak
    text += `U ${workplace}, ${new Date().toLocaleDateString('bs-BA')}.\n\n`;
    text += `Poslodavac: ${employer}   Radnik: ${employee}`;

    return text;
  };

  const clearFields = () => setData({
    employer: '', employerJIB: '', employerAddress: '',
    employee: '', employeeJMBG: '', employeeAddress: '',
    jobTitle: '', workplace: '',
    contractCategory: 'ugovor o radu na neodređeno vrijeme',
    startDate: '', endDate: '', salary: '',
    salaryType: 'neto', salaryPeriod: 'mjesečno',
    probation: false, probationPeriod: '',
    noticePeriod: '', confidentiality: false,
    employerObligations: '', employeeObligations: '',
    annualLeave: '', overtimePolicy: '', travelExpenses: ''
  });

  return (
    <div className="container">
      <h2 className="subsection-title">Generator ugovora o radu</h2>
      {/* Entitet selector */}
      <div className="card field-group">
        <label className="field-label">Entitet:</label>
        <select className="field-select" value={entity} onChange={e => setEntity(e.target.value)}>
          <option value="FBIH">Federacija BiH</option>
          <option value="RS">Republika Srpska</option>
        </select>
      </div>

      {/* Forma */}
      <form className="form-container">
        <div className="card">
          {/* Poslodavac */}
          <div className="field-group">
            <label className="field-label">Poslodavac:</label>
            <input name="employer" className="field-input" value={data.employer} onChange={handleChange} placeholder="Naziv firme" />
          </div>
          <div className="field-group">
            <label className="field-label">JIB poslodavca:</label>
            <input name="employerJIB" className="field-input" value={data.employerJIB} onChange={handleChange} placeholder="JIB" />
          </div>
          <div className="field-group">
            <label className="field-label">Adresa poslodavca:</label>
            <input name="employerAddress" className="field-input" value={data.employerAddress} onChange={handleChange} placeholder="Adresa" />
          </div>
          {/* Radnik */}
          <div className="field-group">
            <label className="field-label">Radnik:</label>
            <input name="employee" className="field-input" value={data.employee} onChange={handleChange} placeholder="Ime i prezime" />
          </div>
          <div className="field-group">
            <label className="field-label">JMBG radnika:</label>
            <input name="employeeJMBG" className="field-input" value={data.employeeJMBG} onChange={handleChange} placeholder="JMBG" />
          </div>
          <div className="field-group">
            <label className="field-label">Adresa radnika:</label>
            <input name="employeeAddress" className="field-input" value={data.employeeAddress} onChange={handleChange} placeholder="Adresa" />
          </div>

          {/* Ostala polja */}
          <div className="field-group">
            <label className="field-label">Radno mjesto / titula:</label>
            <input name="jobTitle" className="field-input" value={data.jobTitle} onChange={handleChange} placeholder="Naziv pozicije" />
          </div>
          <div className="field-group">
            <label className="field-label">Mjesto rada:</label>
            <input name="workplace" className="field-input" value={data.workplace} onChange={handleChange} placeholder="Grad / adresa" />
          </div>
          <div className="field-group">
            <label className="field-label">Vrsta ugovora:</label>
            <select name="contractCategory" className="field-select" value={data.contractCategory} onChange={handleChange}>
              <option value="ugovor o radu na neodređeno vrijeme">Ugovor o radu na neodređено vrijeme</option>
              <option value="ugovor o radu na određено vrijeme">Ugovor o radu na одређено vrijeme</option>
              <option value="ugovor o djelu">Ugovor o djelu</option>
              <option value="autorski ugovor">Autorski ugovor</option>
              <option value="menadžerski ugovor">Menadžerski ugovor</option>
              <option value="privremeni i povremeni poslovi">Privremeni i povремeni poslovi</option>
            </select>
          </div>
          {data.contractCategory.includes('odreђено') && (
            <div className="field-group">
              <label className="field-label">Datum završetka:</label>
              <input name="endDate" type="date" className="field-input" value={data.endDate} onChange={handleChange} />
            </div>
          )}
          <div className="field-group">
            <label className="field-label">Datum početka:</label>
            <input name="startDate" type="date" className="field-input" value={data.startDate} onChange={handleChange} />
          </div>
          <div className="field-group">
            <label className="field-label">Plata (KM):</label>
            <input name="salary" type="number" className="field-input" value={data.salary} onChange={handleChange} placeholder="Iznos u KM" />
          </div>
          <div className="field-group">
            <label className="field-label">Tip plate:</label>
            <select name="salaryType" className="field-select" value={data.salaryType} onChange={handleChange}>
              <option value="neto">Neto plata</option>
              <option value="bruto">Bruto plata</option>
            </select>
          </div>
          <div className="field-group">
            <label className="field-label">Period isplate:</label>
            <select name="salaryPeriod" className="field-select" value={data.salaryPeriod} onChange={handleChange}>
              <option value="mjesečno">Mjesečno</option>
              <option value="sedmično">Sedмиčno</option>
              <option value="dnevno">Dnevno</option>
            </select>
          </div>
          <div className="field-group">
            <label className="field-label">
              <input name="probation" type="checkbox" checked={data.probation} onChange={handleChange} /> Probni rad
            </label>
          </div>
          {data.probation && (
            <div className="field-group">
              <label className="field-label">Trajanje probnog rada (dani):</label>
              <input name="probationPeriod" type="number" className="field-input" value={data.probationPeriod} onChange={handleChange} placeholder="npr. 30" />
            </div>
          )}
          <div className="field-group">
            <label className="field-label">Otkazni rok (dani):</label>
            <input name="noticePeriod" type="number" className="field-input" value={data.noticePeriod} onChange={handleChange} placeholder="npr. 30" />
          </div>
          <div className="field-group">
            <label className="field-label">Obaveze poslodavca:</label>
            <textarea name="employerObligations" className="field-input" value={data.employerObligations} onChange={handleChange} placeholder="Opis obaveza poslodavca" />
          </div>
          <div className="field-group">
            <label className="field-label">Obaveze radnika:</label>
            <textarea name="employeeObligations" className="field-input" value={data.employeeObligations} onChange={handleChange} placeholder="Opis obaveza radnika" />
          </div>
          <div className="field-group">
            <label className="field-label">Godišnji odmor (dani):</label>
            <input name="annualLeave" type="number" className="field-input" value={data.annualLeave} onChange={handleChange} placeholder="Broj dana" />
          </div>
          <div className="field-group">
            <label className="field-label">Prekovremeni rad:</label>
            <textarea name="overtimePolicy" className="field-input" value={data.overtimePolicy} onChange={handleChange} placeholder="Policy prekovremenih sati" />
          </div>
          <div className="field-group">
            <label className="field-label">Putni troškovi:</label>
            <textarea name="travelExpenses" className="field-input" value={data.travelExpenses} onChange={handleChange} placeholder="Policy putnih troškova" />
          </div>
        </div>
      </form>

      {/* Pregled i akcije */}
      <div className="results-container">
        <div className="card results-section">
          <h3 className="subsection-title">Pregled ugovora</h3>
          <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{buildContract()}</pre>
          <button className="clear-button" onClick={() => window.print()}>Printaj ugovor</button>
          <button className="clear-button" onClick={clearFields} style={{ marginLeft: '1rem' }}>Očisti</button>
        </div>
      </div>
    </div>
  );
}
